import React, { useState, useEffect } from 'react';
import { Utensils, Loader2, Sparkles, AlertCircle, ArrowRightLeft, Clock, Save, Check, ChevronRight } from 'lucide-react';
import { FeatureContext, UserProfile, NutriHealPlan } from '../types';
import { generateDietPlan } from '../services/geminiService';
import { saveMealPlan, getCurrentUser } from '../services/backendService';
import NutriHealRecipeCard from './NutriHealRecipeCard';
import logger from "@/utils/logger";

interface NutriHealProps {
  activeContext?: FeatureContext | null;
  userProfile: UserProfile | null;
  onBack?: () => void;
  consultationResponse?: string | null;
  cache?: React.MutableRefObject<Map<string, any>>;
}

const NutriHeal: React.FC<NutriHealProps> = ({ activeContext, userProfile, onBack, consultationResponse, cache }) => {
  const [plan, setPlan] = useState<NutriHealPlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const loadPlan = async (query: string) => {
    // Check cache
    if (cache?.current.has(query)) {
      const cachedData = cache.current.get(query);
      if (cachedData && cachedData.guidelines) {
        setPlan(cachedData as NutriHealPlan);
        return;
      }
    }

    setLoading(true);
    setSaveSuccess(false);
    try {
      const data = await generateDietPlan(query, userProfile, consultationResponse);
      if (data && 'guidelines' in data) {
        const nutriPlan = data as NutriHealPlan;
        setPlan(nutriPlan);
        if (cache) cache.current.set(query, nutriPlan);
      }
    } catch (e) {
      logger.error("NutriHeal Load Error:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const user = getCurrentUser();
    if (!user) {
      alert("Please sign in to save plans.");
      return;
    }
    if (!plan) return;

    setSaveLoading(true);
    try {
      const result = await saveMealPlan(user, plan, plan.ailment);
      if (result) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 5000);
      }
    } catch (e) {
      logger.error("Save Error:", e);
    } finally {
      setSaveLoading(false);
    }
  };

  useEffect(() => {
    if (activeContext) {
      const context = activeContext as any;
      if (context.cachedNutriHeal) {
        setPlan(context.cachedNutriHeal);
        setSaveSuccess(true);
      } else if (activeContext.id) {
        loadPlan(activeContext.id);
      }
    } else {
      setPlan(null);
    }
  }, [activeContext]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 bg-white h-full">
        <Loader2 className="animate-spin text-[#E76F51] w-12 h-12 mb-4" />
        <p className="text-orange-900 font-bold font-serif text-xl">Chef Nani is preparing your Food Intelligence...</p>
        <p className="text-orange-600/60 text-sm mt-2">Curating high-quality recipes for your constitution</p>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="flex flex-col items-center justify-center py-32 bg-white h-full opacity-20">
        <Utensils size={64} className="mb-4" />
        <p className="font-serif text-lg">No healing plan selected</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white overflow-y-auto">
      {/* Sticky Header */}
      <div className="bg-[#FFF8F0] border-b-2 border-[#E76F51] p-4 md:p-6 flex items-center justify-between sticky top-0 z-30 shadow-sm gap-2">
        <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1 mr-2">
          {onBack && (
            <button onClick={onBack} className="flex items-center gap-1 p-2 rounded-xl text-orange-900/60 hover:text-[#E76F51] transition-colors shrink-0">
              <ChevronRight className="rotate-180" size={20} />
              <span className="text-xs font-bold uppercase tracking-widest hidden md:inline">Back</span>
            </button>
          )}
          <div className="shrink-0 w-10 h-10 md:w-12 md:h-12 bg-[#E76F51] rounded-2xl flex items-center justify-center text-white shadow-lg shadow-orange-100">
            <Utensils size={20} className="md:w-6 md:h-6" />
          </div>
          <div className="min-w-0">
            <h1 className="font-serif text-base md:text-2xl font-bold text-gray-900 leading-tight truncate md:whitespace-normal">{plan.ailment}</h1>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
              <span className="text-[#E76F51] text-[9px] md:text-[10px] font-black uppercase tracking-[0.1em] md:tracking-[0.2em]">{plan.category}</span>
              <span className="w-1 h-1 rounded-full bg-gray-300 hidden md:inline" />
              <span className="text-gray-500 text-[9px] md:text-[10px] font-bold uppercase tracking-widest flex items-center gap-1">
                <Clock size={10} /> {plan.duration}{plan.duration === 'Nutrition Guidance' ? '' : ' Protocol'}
              </span>
            </div>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saveLoading || saveSuccess}
          className={`flex items-center gap-2 px-3 py-2.5 rounded-xl font-bold text-xs transition-all shadow-md shrink-0 ${
            saveSuccess 
              ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' 
              : 'bg-[#E76F51] text-white hover:bg-[#d45d41]'
          }`}
        >
          {saveLoading ? <Loader2 size={12} className="animate-spin" /> : saveSuccess ? <Check size={12} /> : <Save size={12} />}
          <span className="hidden md:inline">{saveSuccess ? "Stored in Library" : "Save Guidance"}</span>
          <span className="inline md:hidden">{saveSuccess ? "Saved" : "Save"}</span>
        </button>
      </div>

      <div className="px-4 py-6 md:px-12 md:py-6 max-w-6xl mx-auto w-full space-y-12">
        {/* Block 1: AI Personalization */}
        <section className="bg-orange-50/30 rounded-[1.5rem] md:rounded-[2.5rem] p-5 md:p-12 border border-orange-100/50 relative overflow-hidden">
          <Sparkles className="absolute top-8 right-8 text-[#E76F51]/10 w-24 h-24 rotate-12" />
          <div className="relative z-10 max-w-3xl">
            <h2 className="font-serif text-2xl font-bold text-orange-950 mb-4">Nani's Personal Note</h2>
            <p className="text-orange-900/80 leading-relaxed text-lg font-medium italic">
              "{plan.personalizationNote}"
            </p>
          </div>
        </section>

        {/* Block 2: Static Guidelines */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Embrace */}
          <div className="bg-emerald-50/30 rounded-[1.5rem] md:rounded-[2rem] p-5 md:p-8 border border-emerald-100/50">
            <h3 className="text-base font-bold tracking-wide text-teal-700 flex items-center gap-2 mb-3">
              <span className="text-lg">✅</span> Foods to Embrace
            </h3>
            <ul className="space-y-4">
              {plan.guidelines.embrace.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-emerald-900/80 font-medium">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Avoid */}
          <div className="bg-red-50/30 rounded-[1.5rem] md:rounded-[2rem] p-5 md:p-8 border border-red-100/50">
            <h3 className="text-base font-bold tracking-wide text-red-600 flex items-center gap-2 mb-3">
              <span className="text-lg">🚫</span> Foods to Avoid
            </h3>
            <ul className="space-y-4">
              {plan.guidelines.avoid.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-red-900/80 font-medium">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Block 3: Smart Switches (Text-Only Design) */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center text-amber-700 shadow-sm">
              <ArrowRightLeft size={20} />
            </div>
            <h3 className="text-lg font-bold text-amber-950 uppercase tracking-widest">Smart Switches</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {plan.guidelines.switches.map((item, i) => {
              const parts = item.split(' \u2192 ');
              if (parts.length < 2) return null;
              
              const from = parts[0];
              const to = parts[1];

              return (
                <div key={i} className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                  {/* FROM */}
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-red-500 text-base flex-shrink-0">✕</span>
                    <span className="text-red-700 text-sm font-medium line-through truncate">{from}</span>
                  </div>
                  {/* Arrow */}
                  <span className="text-amber-500 font-bold text-lg flex-shrink-0">→</span>
                  {/* TO */}
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-green-500 text-base flex-shrink-0">✓</span>
                    <span className="text-green-700 text-sm font-semibold truncate">{to}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Block 4: Primary Recipes */}
        <section>
          <div className="flex items-center justify-between mb-8 border-b border-gray-100 pb-4">
            <h2 className="font-serif text-3xl font-bold text-gray-900">Healing Recipes</h2>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Fixed Library • Verified Quality</p>
          </div>
          <div className={`grid gap-6 ${
            plan.primaryRecipes.length === 1 
              ? 'grid-cols-1' 
              : plan.primaryRecipes.length === 2 
                ? 'grid-cols-1 md:grid-cols-2' 
                : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'
          }`}>
            {plan.primaryRecipes.map((recipe) => (
              <div 
                key={recipe.id}
                className={plan.primaryRecipes.length === 1 ? 'max-w-lg mx-auto w-full' : ''}
              >
                <NutriHealRecipeCard recipe={recipe} />
              </div>
            ))}
          </div>
        </section>

        {/* Block 4: Herbal Tonics */}
        {plan.herbalTonics.length > 0 && (
          <section className="bg-orange-50/20 rounded-[1.5rem] md:rounded-[3rem] p-5 md:p-12 border border-orange-100/30">
            <div className="mb-8">
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-2">Supportive Herbal Tonics</h2>
              <p className="text-sm text-gray-500">Ancient infusions to accelerate your metabolic recovery.</p>
            </div>
            <div className={`grid gap-6 ${
              plan.herbalTonics.length === 1 
                ? 'grid-cols-1' 
                : plan.herbalTonics.length === 2 
                  ? 'grid-cols-1 md:grid-cols-2' 
                  : 'grid-cols-1 md:grid-cols-3'
            }`}>
              {plan.herbalTonics.map((tonic) => (
                <div 
                  key={tonic.id}
                  className={plan.herbalTonics.length === 1 ? 'max-w-lg mx-auto w-full' : ''}
                >
                  <NutriHealRecipeCard recipe={tonic} />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Footer Guidance */}
        <footer className="pt-12 border-t border-gray-100">
          <div className="bg-gray-50 p-5 md:p-6 rounded-2xl md:rounded-3xl flex flex-col md:flex-row items-center gap-6 justify-between">
            <div className="flex items-center gap-4 text-gray-500">
              <AlertCircle size={24} className="text-orange-300 shrink-0" />
              <p className="text-xs leading-relaxed">
                <strong>Educational Guidance:</strong> These recommendations are based on traditional Ayurvedic principles and are not a substitute for medical advice. If symptoms persist or worsen, please consult your physician.
              </p>
            </div>
            <div className="flex items-center gap-4 text-[10px] font-black uppercase tracking-[0.2em] text-[#E76F51]">
              <span>Quality: Triple Verified</span>
              <span className="w-1 h-1 rounded-full bg-orange-200" />
              <span>Cultural Integrity: 100%</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default NutriHeal;
